import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, Facebook, Twitter, ArrowLeft } from 'lucide-react';
import { BLOG_POSTS } from '../constants';

export const BlogPostPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  // Find blog post by slug
  const blogPost = BLOG_POSTS.find((post) => post.slug === slug);

  // If blog post not found, redirect or show error
  if (!blogPost) {
    return (
      <div className="pt-32 pb-24 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-heading-section font-bold mb-4">
            Blog Post Not Found
          </h1>
          <button
            onClick={() => navigate("/blog")}
            className="px-6 py-3 bg-[#0055aa] text-white rounded-lg hover:bg-[#004488] transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  // Get related posts (exclude current post)
  const relatedPosts = BLOG_POSTS.filter((post) => post.id !== blogPost.id).slice(0, 3);

  const handlePostClick = (postSlug: string) => {
    navigate(`/blog/${postSlug}`);
  };

  // Split title for gradient effect (simple split on first space or common words)
  const titleParts = blogPost.title.split(' ');
  const titleFirstPart = titleParts.slice(0, Math.ceil(titleParts.length / 2)).join(' ');
  const titleSecondPart = titleParts.slice(Math.ceil(titleParts.length / 2)).join(' ');

  return (
    <div className="pt-32 min-h-screen bg-white font-sans relative">
      
      {/* Back Button - Full width container for left alignment */}
      <div className="absolute top-[3%] left-6 hidden lg:block">
        <button
          onClick={() => navigate("/blog")}
          className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200 shadow-sm group"
          aria-label="Back to blog"
        >
          <ArrowLeft 
            size={18} 
            className="text-gray-700 md:text-gray-800 group-hover:-translate-x-0.5 transition-transform duration-200" 
            strokeWidth={2.5} 
          />
        </button>
      </div>
      
      {/* Title Section */}
      <div className="container mx-auto px-4 md:px-6 mb-12 text-center max-w-4xl">
        <h1 className="text-heading-large font-normal text-[#0055aa]">
          {titleFirstPart} <span className="text-black">{titleSecondPart}</span>
        </h1>
      </div>

      {/* Hero Image */}
      <div className="w-full h-[400px] md:h-[600px] overflow-hidden mb-16">
         <img 
            src={blogPost.image} 
            alt={blogPost.title} 
            className="w-full h-full object-cover"
         />
      </div>

      {/* Content Container */}
      <div className="container mx-auto px-6 max-w-[800px]">
         
         {/* Author & Share Header */}
         <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-200 pb-8 mb-10 gap-6">
            <div className="flex items-center gap-4">
               <img src={blogPost.avatar} className="w-12 h-12 rounded-full object-cover" alt={blogPost.author} />
               <div>
                  <h4 className="text-body font-bold text-gray-900 uppercase tracking-wide">{blogPost.author.toUpperCase()}</h4>
                  <p className="text-body text-gray-500 mt-1 font-medium">{blogPost.date || '20 Jan 2025'} · {blogPost.readTime}</p>
               </div>
            </div>
            
            <div className="flex gap-3">
               <button className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-700">
                  <Facebook size={16} fill="currentColor" strokeWidth={0} />
               </button>
               <button className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-700">
                  <Twitter size={16} fill="currentColor" strokeWidth={0} />
               </button>
            </div>
         </div>

         {/* Article Content */}
         <div className="prose prose-lg max-w-none text-gray-600 mb-12">
            {blogPost.content?.paragraphs?.map((paragraph, index) => (
               <React.Fragment key={index}>
                  <p className={index === 0 ? "mb-6 leading-relaxed" : "mb-12 leading-relaxed"}>
                     {paragraph}
                  </p>
                  {/* Show image after first paragraph */}
                  {index === 0 && (
                     <div className="rounded-2xl overflow-hidden mb-10 h-[400px] mt-8">
                        <img 
                           src={blogPost.image} 
                           alt={blogPost.title} 
                           className="w-full h-full object-cover"
                        />
                     </div>
                  )}
               </React.Fragment>
            ))}

            {blogPost.content?.sections?.map((section, sectionIndex) => {
               const headingParts = section.heading.split(' ');
               const headingFirstPart = headingParts.slice(0, Math.ceil(headingParts.length / 2)).join(' ');
               const headingSecondPart = headingParts.slice(Math.ceil(headingParts.length / 2)).join(' ');

               return (
                  <div key={sectionIndex}>
                     <h2 className="text-heading-section font-normal text-[#0055aa] mb-6">
                        {headingFirstPart} <span className="text-black">{headingSecondPart}</span>
                     </h2>
                     
                     {section.paragraphs?.map((paragraph, pIndex) => (
                        <p key={pIndex} className={pIndex === 0 ? "mb-8 text-body" : "mb-8 text-body"}>
                           {paragraph}
                        </p>
                     ))}

                     {/* Show image after first paragraph of section */}
                     {section.paragraphs && section.paragraphs.length > 0 && (
                        <div className="rounded-2xl overflow-hidden mb-10 h-[400px] mt-6">
                           <img 
                              src={blogPost.image} 
                              alt={section.heading} 
                              className="w-full h-full object-cover"
                           />
                        </div>
                     )}

                     {section.listItems && (
                        <ul className="list-disc pl-5 space-y-3 mb-8 text-body text-gray-700 marker:text-black">
                           {section.listItems.map((item, itemIndex) => (
                              <li key={itemIndex} className="pl-2">{item}</li>
                           ))}
                        </ul>
                     )}
                  </div>
               );
            })}

            {blogPost.content?.closing && (
               <div className="mt-12 mb-12">
                  {blogPost.content.closing.thanks && (
                     <p className="text-gray-900 font-medium">{blogPost.content.closing.thanks}</p>
                  )}
                  {blogPost.content.closing.author && (
                     <p className="text-gray-900 font-medium">{blogPost.content.closing.author}</p>
                  )}
               </div>
            )}
         </div>

         {/* Bottom Share Bar */}
         <div className="border-t border-b border-gray-200 py-6 mb-20 flex justify-between">
            <button className="flex-1 flex items-center justify-center gap-2 text-body font-bold text-gray-800 hover:text-[#0055aa] transition-colors border-r border-gray-200">
               <Facebook size={16} fill="currentColor" strokeWidth={0} /> Share on Facebook
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 text-body font-bold text-gray-800 hover:text-[#0055aa] transition-colors">
               <Twitter size={16} fill="currentColor" strokeWidth={0} /> Share on Twitter
            </button>
         </div>
      </div>

      {/* What to Read Next */}
      <div className="bg-[#FAFAFA] py-24">
         <div className="container mx-auto px-6">
            <h2 className="text-heading-large font-normal text-center mb-16 text-black font-neue-haas">
               WHAT TO READ NEXT
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
               {relatedPosts.map((post) => (
                  <div key={post.id} className="group cursor-pointer bg-white rounded-[24px] overflow-hidden hover:shadow-lg transition-all duration-300" onClick={() => handlePostClick(post.slug)}>
                     <div className="h-[240px] overflow-hidden relative">
                        <img 
                           src={post.image} 
                           alt={post.title} 
                           className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute top-6 left-6">
                           <span className="bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black rounded-sm shadow-sm">{post.tag}</span>
                        </div>
                     </div>
                     
                     <div className="p-8">
                        <h3 className="text-heading-sub font-medium text-gray-900 mb-6 group-hover:text-[#0055aa] transition-colors">
                           {post.title}
                        </h3>
                        
                        <div className="flex items-center justify-between border-t border-gray-100 pt-6">
                           <div className="flex items-center gap-3">
                              <img src={post.avatar} className="w-8 h-8 rounded-full object-cover" alt={post.author} />
                              <div>
                                 <span className="block text-body font-bold text-gray-900">{post.author}</span>
                                 <span className="block text-body text-gray-500">{post.role}</span>
                              </div>
                           </div>
                           <div className="flex items-center gap-1.5 text-gray-400 text-body font-medium">
                              <Clock size={14} />
                              <span>{post.readTime}</span>
                           </div>
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>

    </div>
  );
};